import React, { Children, useEffect, useState, useCallback, useRef, useContext, createContext } from 'react';
import styled, { css } from 'styled-components';
import { ChevronRight } from '@styled-icons/material/ChevronRight';
import { spacing, fontStyle, breakpoints, themeLight, icons } from '@naf/theme';
import { propValidationConfig } from './propValidationConfig';
const getURLQuery = () => {
  if (typeof window !== 'undefined') {
    const parsedLocation = new URL(window.location.href);
    if (parsedLocation && !!parsedLocation.search) {
      return '?' + parsedLocation.search;
    }
  }
  return '';
};
export let BreadcrumbVariant = /*#__PURE__*/function (BreadcrumbVariant) {
  BreadcrumbVariant["Standard"] = "standard";
  BreadcrumbVariant["Inverted"] = "inverted";
  return BreadcrumbVariant;
}({});
const ChildContext = /*#__PURE__*/createContext(0);
const VariantContext = /*#__PURE__*/createContext(BreadcrumbVariant.Standard);
export const Breadcrumbs = ({
  children,
  className,
  variant = BreadcrumbVariant.Standard
}) => {
  const [oneUp, setOneUp] = useState(false);
  const childCount = useRef(Children.count(children));
  const setBreadcrumbVariant = useCallback(() => {
    if (childCount.current > 1 && window.innerWidth <= parseInt(breakpoints.s)) {
      setOneUp(true);
    } else setOneUp(false);
  }, [childCount]);
  useEffect(() => {
    window.addEventListener('resize', setBreadcrumbVariant);
    return () => window.removeEventListener('resize', setBreadcrumbVariant);
  }, [setBreadcrumbVariant]);
  if (childCount.current > 1 && window.innerWidth <= parseInt(breakpoints.s) || oneUp) {
    const child = Children.toArray(children)[childCount.current - 2];
    return /*#__PURE__*/React.createElement(Navigation, {
      className: className
    }, /*#__PURE__*/React.createElement(ChildContext.Provider, {
      value: childCount.current
    }, /*#__PURE__*/React.createElement(VariantContext.Provider, {
      value: variant
    }, child)));
  } else return /*#__PURE__*/React.createElement(Navigation, {
    className: className
  }, /*#__PURE__*/React.createElement(ChildContext.Provider, {
    value: childCount.current
  }, /*#__PURE__*/React.createElement(VariantContext.Provider, {
    value: variant
  }, children)));
};
export const BreadcrumbItem = ({
  children,
  href,
  getQuery = false
}) => {
  if (getQuery) {
    href += getURLQuery();
  }
  const childCount = useContext(ChildContext);
  const variant = useContext(VariantContext);
  const fullPathItem = () => {
    return /*#__PURE__*/React.createElement(FullPathLink, {
      variant: variant
    }, /*#__PURE__*/React.createElement(PathElement, {
      as: href ? 'a' : 'div',
      href: href
    }, children), /*#__PURE__*/React.createElement(ChevronRight, {
      size: 16
    }));
  };
  const oneUpItem = () => {
    return /*#__PURE__*/React.createElement(OneUpLink, {
      variant: variant
    }, /*#__PURE__*/React.createElement(PathElement, {
      as: href ? 'a' : 'div',
      href: href
    }, /*#__PURE__*/React.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      width: "16",
      height: "20",
      viewBox: "0 0 16 20"
    }, /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
      d: "M10,6,6,10,10,14",
      fill: "none",
      stroke: "currentColor",
      strokeLinejoin: "round",
      strokeMiterlimit: "10",
      strokeWidth: "1"
    })), /*#__PURE__*/React.createElement("line", {
      opacity: "0",
      x1: "2",
      x2: "10",
      y1: "10",
      y2: "10",
      fill: "none",
      stroke: "currentColor",
      strokeLinejoin: "round",
      strokeMiterlimit: "10",
      strokeWidth: "1"
    })), /*#__PURE__*/React.createElement("span", null, children)));
  };
  if (childCount > 1 && window.innerWidth > parseInt(breakpoints.s)) {
    return fullPathItem();
  }
  return oneUpItem();
};
export default Breadcrumbs;
const PathElement = styled.a.withConfig({
  displayName: "breadcrumbs__PathElement",
  componentId: "sc-4p2if-0"
})(["text-decoration:underline;"]);
const Navigation = styled.div.withConfig({
  displayName: "breadcrumbs__Navigation",
  componentId: "sc-4p2if-1"
})(["", ";line-height:1;"], fontStyle.bodyText.small);
const FullPathLink = styled.span.withConfig(propValidationConfig).withConfig({
  displayName: "breadcrumbs__FullPathLink",
  componentId: "sc-4p2if-2"
})(["display:inline-flex;align-items:center;cursor:pointer;", " ", ""], ({
  variant
}) => variant === BreadcrumbVariant.Standard && css(["", "{color:", ";&:visited{color:", ";}&:hover{text-decoration-line:none;color:", ";}}svg{color:", ";font-size:", ";padding:0 ", ";}&:last-child{cursor:default;svg{display:none;}", "{text-decoration:none;color:", ";}}"], PathElement, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, ({
  theme
}) => theme.typography ? theme.typography.textLink : themeLight.typography.textLink, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, icons.s, spacing.space8, PathElement, ({
  theme
}) => theme.typography ? theme.typography.subtleText : themeLight.typography.subtleText), ({
  variant
}) => variant === BreadcrumbVariant.Inverted && css(["", "{color:", ";&:visited{color:", ";}&:hover{text-decoration-line:none;color:", ";}}svg{color:", ";font-size:", ";padding:0 ", ";}&:last-child{cursor:default;svg{display:none;}", "{text-decoration:none;color:", ";}}"], PathElement, ({
  theme
}) => theme.typography ? theme.typography.defaultTextInverted : themeLight.typography.defaultTextInverted, ({
  theme
}) => theme.typography ? theme.typography.defaultTextInverted : themeLight.typography.defaultTextInverted, ({
  theme
}) => theme.typography ? theme.typography.textLinkInverted : themeLight.typography.textLinkInverted, ({
  theme
}) => theme.typography ? theme.typography.defaultTextInverted : themeLight.typography.defaultTextInverted, icons.s, spacing.space8, PathElement, ({
  theme
}) => theme.typography ? theme.typography.subtleTextInverted : themeLight.typography.subtleTextInverted));
const OneUpLink = styled.span.withConfig(propValidationConfig).withConfig({
  displayName: "breadcrumbs__OneUpLink",
  componentId: "sc-4p2if-3"
})(["display:inline-flex;cursor:pointer;", " ", " svg:not([fill]){fill:currentColor;}svg{-webkit-transform:translateY(0);transform:translateY(0);}svg line{-webkit-transition:opacity 250ms ease 0ms;transition:opacity 250ms ease 0ms;}svg path{-webkit-transition:-webkit-transform 250ms ease;transition:-webkit-transform 250ms ease;transition:transform 250ms ease;transition:transform 250ms ease,-webkit-transform 250ms ease;}"], ({
  variant
}) => variant === BreadcrumbVariant.Standard && css(["", "{display:flex;align-items:center;text-decoration:none;color:", ";svg{font-size:", ";}&:visited{color:", ";}&:hover{text-decoration:none;color:", ";svg line{opacity:1;-webkit-transition-delay:50ms;transition-delay:50ms;}svg path{-webkit-transform:translateX(-", ");transform:translateX(-", ");}}> span{margin-left:", ";text-decoration:none;}}"], PathElement, ({
  theme
}) => theme.typography ? theme.typography.subtleText : themeLight.typography.subtleText, icons.s, ({
  theme
}) => theme.typography ? theme.typography.subtleText : themeLight.typography.subtleText, ({
  theme
}) => theme.typography ? theme.typography.textLink : themeLight.typography.textLink, spacing.space4, spacing.space4, spacing.space4), ({
  variant
}) => variant === BreadcrumbVariant.Inverted && css(["", "{display:flex;align-items:center;text-decoration:none;color:", ";svg{font-size:", ";}&:visited{color:", ";}&:hover{text-decoration:none;color:", ";svg line{opacity:1;-webkit-transition-delay:50ms;transition-delay:50ms;}svg path{-webkit-transform:translateX(-", ");transform:translateX(-", ");}}> span{margin-left:", ";text-decoration:none;}}"], PathElement, ({
  theme
}) => theme.typography ? theme.typography.subtleTextInverted : themeLight.typography.subtleTextInverted, icons.s, ({
  theme
}) => theme.typography ? theme.typography.subtleTextInverted : themeLight.typography.subtleTextInverted, ({
  theme
}) => theme.typography ? theme.typography.textLinkInverted : themeLight.typography.textLinkInverted, spacing.space4, spacing.space4, spacing.space4));
//# sourceMappingURL=breadcrumbs.js.map